import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle, Ban, CheckCircle, ChevronDown, ChevronUp, Clock, Download,
  Hourglass, Loader2, Play, SkipForward, Square, XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { schedulerApi } from '@/services/api/scheduler'
import { getApiErrorMessage } from '@/lib/apiError'
import type {
  PipelineName, PipelineRun, PipelineRunDetail, PipelineSchedule, RunStatus,
  SchedulerConfigUpdate,
} from '@/types/api'
import { cn } from '@/lib/utils'

/** Mirrors the CHECK on pipeline_schedules.max_articles and the Pydantic bounds. */
/** A run that has not finished yet. Mirrors run_log_service.LIVE_STATUSES. */
const LIVE_STATUSES: RunStatus[] = ['queued', 'running']

const CAP_MIN = 1
const CAP_MAX = 2000

const PIPELINE_LABEL: Record<PipelineName, string> = {
  evaluations: 'Evaluations',
  ai_summary: 'AI Summary',
}

const STATUS_STYLE: Record<string, { icon: typeof CheckCircle; className: string }> = {
  queued: { icon: Hourglass, className: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
  success: { icon: CheckCircle, className: 'text-green-700 bg-green-50 border-green-200' },
  failed: { icon: XCircle, className: 'text-red-700 bg-red-50 border-red-200' },
  running: { icon: Loader2, className: 'text-blue-700 bg-blue-50 border-blue-200' },
  skipped: { icon: SkipForward, className: 'text-amber-700 bg-amber-50 border-amber-200' },
  missed: { icon: AlertTriangle, className: 'text-amber-700 bg-amber-50 border-amber-200' },
  cancelled: { icon: Ban, className: 'text-slate-700 bg-slate-100 border-slate-300' },
}

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLE[status] ?? {
    icon: Clock, className: 'text-gray-700 bg-gray-50 border-gray-200',
  }
  const Icon = style.icon
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium',
      style.className,
    )}>
      <Icon className={cn('h-3 w-3', status === 'running' && 'animate-spin')} />
      {status}
    </span>
  )
}

function formatLocal(iso: string | null, timeZone?: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short', ...(timeZone ? { timeZone } : {}),
    })
  } catch {
    return iso
  }
}

function formatDuration(run: PipelineRun): string {
  if (!run.started_at || !run.finished_at) return '—'
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()
  if (Number.isNaN(ms) || ms < 0) return '—'
  const minutes = Math.floor(ms / 60000)
  return minutes >= 1 ? `${minutes}m` : `${Math.round(ms / 1000)}s`
}

/**
 * A run whose slot and start differ by more than a minute waited for the other
 * pipeline's lock. That is the normal case when both are scheduled at 10:00 and
 * it must not read as a fault.
 */
function queuedDelay(run: PipelineRun): string | null {
  if (!run.scheduled_for || !run.started_at) return null
  const ms = new Date(run.started_at).getTime() - new Date(run.scheduled_for).getTime()
  if (Number.isNaN(ms) || ms < 60_000) return null
  return new Date(run.started_at).toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  })
}

function downloadText(text: string, filename: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function RunDetailPanel({
  run, detail,
}: {
  run: PipelineRun
  detail: PipelineRunDetail | null
}) {
  const items = detail?.items ?? []
  const facts: [string, string][] = [
    ['Trigger', run.trigger + (run.parent_run_id ? ` (retry of ${run.parent_run_id})` : '')],
    ['Slot', run.scheduled_for ? formatLocal(run.scheduled_for) : 'not scheduled (manual)'],
    ['Started', formatLocal(run.started_at)],
    ['Finished', formatLocal(run.finished_at)],
    ['Duration', formatDuration(run)],
    ['Cap', run.max_articles === null ? '—' : String(run.max_articles)],
    ['Job', run.job_id ?? '—'],
    ['Started by', run.triggered_by ?? 'the scheduler'],
  ]

  return (
    <div className="space-y-3 border-l-2 border-primary/40 px-4 py-3">
      <div className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-4">
        {facts.map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium break-all">{value}</span>
          </div>
        ))}
      </div>

      {run.error_message && (
        <div>
          <div className="mb-1 text-xs font-semibold text-red-800">Error</div>
          <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded bg-red-50 p-2 text-[11px] text-red-800">
            {run.error_message}
          </pre>
        </div>
      )}

      <div>
        <div className="mb-1 text-xs font-semibold">
          Articles ({items.length})
        </div>
        {detail === null ? (
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </p>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No per-article results were recorded — this run ended before it
            processed anything.
          </p>
        ) : (
          <div className="max-h-64 overflow-y-auto rounded border bg-background">
            {items.map((item, index) => (
              <div
                key={`${item.display_number}-${index}`}
                className="flex flex-wrap items-baseline gap-2 border-b px-2 py-1 text-xs last:border-0"
              >
                <StatusBadge status={item.status} />
                <span className="font-mono">{item.display_number}</span>
                {item.duration_ms !== null && (
                  <span className="text-muted-foreground">
                    {(item.duration_ms / 1000).toFixed(1)}s
                  </span>
                )}
                {item.error_message && (
                  <span className="text-red-700">{item.error_message}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


function ScheduleCard({
  schedule, onSave, onRunNow, isSaving, isTriggering, setError,
}: {
  schedule: PipelineSchedule
  onSave: (pipeline: PipelineName, update: SchedulerConfigUpdate) => void
  onRunNow: (pipeline: PipelineName) => void
  isSaving: boolean
  isTriggering: boolean
  setError: (message: string | null) => void
}) {
  const [runAt, setRunAt] = useState(schedule.run_at)
  const [cap, setCap] = useState(String(schedule.max_articles))
  const [retry, setRetry] = useState(String(schedule.retry_after_minutes))

  const writesToServiceNow = schedule.pipeline === 'ai_summary'
  const lastRun = schedule.last_run

  const commitCap = () => {
    const value = Number(cap)
    if (!Number.isInteger(value) || value < CAP_MIN || value > CAP_MAX) {
      // Say so here rather than spend a round trip; the backend rejects it too.
      setError(`Max articles must be a whole number between ${CAP_MIN} and ${CAP_MAX}`)
      setCap(String(schedule.max_articles))
      return
    }
    if (value !== schedule.max_articles) onSave(schedule.pipeline, { max_articles: value })
  }

  const commitRetry = () => {
    const value = Number(retry)
    if (!Number.isInteger(value) || value < 0 || value > 1440) {
      setError('Retry delay must be a whole number of minutes between 0 and 1440')
      setRetry(String(schedule.retry_after_minutes))
      return
    }
    if (value !== schedule.retry_after_minutes) {
      onSave(schedule.pipeline, { retry_after_minutes: value })
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold">{PIPELINE_LABEL[schedule.pipeline]}</h3>
            {writesToServiceNow && (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                This pipeline writes to ServiceNow
              </p>
            )}
          </div>
          {/*
            The toggle shows the CURRENT state, and the text next to it says what
            that state means. A bare "On"/"Off" button was ambiguous - it read as
            though it might be the action rather than the state.
          */}
          <div className="flex shrink-0 items-center gap-2">
            <div className="text-right">
              <div className={cn(
                'text-sm font-medium',
                schedule.enabled ? 'text-green-700' : 'text-muted-foreground',
              )}>
                {schedule.enabled ? 'Runs daily' : 'Paused'}
              </div>
              <div className="text-xs text-muted-foreground">
                {schedule.enabled
                  ? `at ${schedule.run_at}`
                  : 'will not run on its own'}
              </div>
            </div>
            {isSaving ? (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            ) : (
              <Switch
                checked={schedule.enabled}
                aria-label={`${PIPELINE_LABEL[schedule.pipeline]} daily schedule`}
                onCheckedChange={(next) =>
                  onSave(schedule.pipeline, { enabled: next })}
              />
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs font-medium">
            Daily at
            <Input
              type="time"
              value={runAt}
              className="mt-1"
              onChange={(e) => setRunAt(e.target.value)}
              onBlur={() => {
                if (runAt && runAt !== schedule.run_at) {
                  onSave(schedule.pipeline, { run_at: runAt })
                }
              }}
            />
          </label>
          <label className="text-xs font-medium">
            Max articles per run ({CAP_MIN}–{CAP_MAX})
            <Input
              type="number" min={CAP_MIN} max={CAP_MAX} value={cap}
              className="mt-1"
              onChange={(e) => setCap(e.target.value)}
              onBlur={commitCap}
            />
          </label>
          <label className="text-xs font-medium">
            Retry once after (min, 0 = off)
            <Input
              type="number" min={0} max={1440} value={retry}
              className="mt-1"
              onChange={(e) => setRetry(e.target.value)}
              onBlur={commitRetry}
            />
          </label>
        </div>

        <p className="text-xs text-muted-foreground">
          Timezone <span className="font-medium">{schedule.timezone}</span>
          {schedule.next_due
            ? <> · next run {formatLocal(schedule.next_due, schedule.timezone)}</>
            : <> · disabled, no next run scheduled</>}
        </p>

        {lastRun ? (
          <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Last run</span>
            <StatusBadge status={lastRun.status} />
            <span>{formatLocal(lastRun.started_at)}</span>
            <span className="text-muted-foreground">
              {lastRun.ok_articles}/{lastRun.total_articles} ok
              {lastRun.failed_articles > 0 && ` · ${lastRun.failed_articles} failed`}
            </span>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No runs yet.</p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            size="sm" variant="outline" disabled={isTriggering}
            onClick={() => onRunNow(schedule.pipeline)}
          >
            {isTriggering
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Play className="mr-2 h-4 w-4" />}
            Run now
          </Button>
          {lastRun && (
            <Button
              size="sm" variant="outline"
              onClick={async () => {
                try {
                  const text = await schedulerApi.downloadRunLog(lastRun.id)
                  downloadText(text, `run_${lastRun.id}_${lastRun.pipeline}.txt`)
                } catch (e) {
                  setError(getApiErrorMessage(e))
                }
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Last run log
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function SchedulesTab({
  setError, setSuccess,
}: {
  setError: (message: string | null) => void
  setSuccess: (message: string | null) => void
}) {
  const queryClient = useQueryClient()
  const [pipelineFilter, setPipelineFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [expandedRun, setExpandedRun] = useState<number | null>(null)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['scheduler', 'config'] })
    queryClient.invalidateQueries({ queryKey: ['scheduler', 'runs'] })
  }

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['scheduler', 'config'],
    queryFn: () => schedulerApi.getConfig(),
    refetchInterval: 30_000,
  })

  const { data: runsData, isLoading: runsLoading } = useQuery({
    queryKey: ['scheduler', 'runs', pipelineFilter, statusFilter],
    queryFn: () => schedulerApi.listRuns({
      pipeline: pipelineFilter === 'all' ? undefined : (pipelineFilter as PipelineName),
      status: statusFilter === 'all' ? undefined : statusFilter,
      limit: 50,
    }),
    // Poll faster while a run is live so Stop and progress feel responsive.
    refetchInterval: (query) =>
      (query.state.data?.runs ?? []).some((r) => LIVE_STATUSES.includes(r.status))
        ? 5_000
        : 30_000,
  })

  const saveMutation = useMutation({
    mutationFn: ({ pipeline, update }: {
      pipeline: PipelineName; update: SchedulerConfigUpdate
    }) => schedulerApi.updateConfig(pipeline, update),
    onSuccess: () => { setSuccess('Schedule updated'); invalidate() },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const triggerMutation = useMutation({
    mutationFn: (pipeline: PipelineName) => schedulerApi.triggerRun(pipeline),
    onSuccess: () => {
      setSuccess('Run started — watch the history below for progress')
      invalidate()
    },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const stopMutation = useMutation({
    mutationFn: (runId: number) => schedulerApi.stopRun(runId),
    onSuccess: (data) => { setSuccess(data.message); invalidate() },
    onError: (e: Error) => setError(getApiErrorMessage(e)),
  })

  const { data: runDetail } = useQuery({
    queryKey: ['scheduler', 'run', expandedRun],
    queryFn: () => schedulerApi.getRun(expandedRun as number),
    enabled: expandedRun !== null,
  })

  const downloadRange = async () => {
    const to = new Date()
    const from = new Date(to.getTime() - 89 * 24 * 3600 * 1000)
    const iso = (d: Date) => d.toISOString().slice(0, 10)
    try {
      const text = await schedulerApi.downloadRangeLog(
        iso(from), iso(to),
        pipelineFilter === 'all' ? undefined : (pipelineFilter as PipelineName),
      )
      downloadText(text, `pipeline_runs_${iso(from)}_${iso(to)}.txt`)
    } catch (e) {
      setError(getApiErrorMessage(e))
    }
  }

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  const runs = runsData?.runs ?? []

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        {(config?.schedules ?? []).map((schedule) => (
          <ScheduleCard
            key={schedule.pipeline}
            schedule={schedule}
            setError={setError}
            isSaving={saveMutation.isPending
              && saveMutation.variables?.pipeline === schedule.pipeline}
            isTriggering={triggerMutation.isPending
              && triggerMutation.variables === schedule.pipeline}
            onSave={(pipeline, update) => saveMutation.mutate({ pipeline, update })}
            onRunNow={(pipeline) => triggerMutation.mutate(pipeline)}
          />
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        When both pipelines are due at the same time they run one after the other,
        Evaluations first — they share one Azure OpenAI deployment.
      </p>

      <Card>
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Recent runs</h3>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
                <SelectTrigger className="h-8 w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All pipelines</SelectItem>
                  <SelectItem value="evaluations">Evaluations</SelectItem>
                  <SelectItem value="ai_summary">AI Summary</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-8 w-[140px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="skipped">Skipped</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                  <SelectItem value="missed">Missed</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={downloadRange}>
                <Download className="mr-2 h-4 w-4" />
                Download 90 days
              </Button>
            </div>
          </div>

          {runsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : runs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No runs yet. Both schedules start disabled — turn one on above, or
              press Run now.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Started</th>
                    <th className="py-2 pr-3 font-medium">Pipeline</th>
                    <th className="py-2 pr-3 font-medium">Trigger</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-3 font-medium">Articles</th>
                    <th className="py-2 pr-3 font-medium">Took</th>
                    <th className="py-2 font-medium">Log</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => {
                    const queued = queuedDelay(run)
                    const isExpanded = expandedRun === run.id
                    return (
                      <Fragment key={run.id}>
                      <tr
                        className={cn(
                          'border-b align-top last:border-0 hover:bg-muted/30',
                          isExpanded && 'bg-muted/40',
                        )}
                      >
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {formatLocal(run.started_at)}
                          {run.parent_run_id && (
                            <div className="text-xs text-muted-foreground">
                              retry of run {run.parent_run_id}
                            </div>
                          )}
                          {queued && (
                            <div className="text-xs text-muted-foreground">
                              queued behind the other pipeline, started {queued}
                            </div>
                          )}
                          {run.status === 'queued' && !run.stop_requested && (
                            <div className="text-xs text-indigo-700">
                              waiting for the other pipeline to finish
                            </div>
                          )}
                          {run.stop_requested && LIVE_STATUSES.includes(run.status) && (
                            <div className="text-xs text-amber-700">
                              {run.status === 'queued'
                                ? 'stopping — it had not started yet'
                                : 'stopping after the current article finishes'}
                            </div>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline">
                            {PIPELINE_LABEL[run.pipeline] ?? run.pipeline}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{run.trigger}</td>
                        <td className="py-2 pr-3">
                          <StatusBadge status={run.status} />
                          {run.error_message && (
                            <pre className="mt-1 max-w-md overflow-x-auto whitespace-pre-wrap rounded bg-red-50 p-2 text-[11px] text-red-800">
                              {run.error_message}
                            </pre>
                          )}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          {run.ok_articles}/{run.total_articles}
                          {run.failed_articles > 0 && (
                            <span className="text-red-700"> · {run.failed_articles} failed</span>
                          )}
                          {run.skipped_articles > 0 && (
                            <span className="text-muted-foreground"> · {run.skipped_articles} skipped</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">{formatDuration(run)}</td>
                        <td className="py-2 whitespace-nowrap">
                          <div className="flex gap-1">
                            {LIVE_STATUSES.includes(run.status) && (
                              <Button
                                size="sm" variant="destructive"
                                className="h-7 px-2 text-xs"
                                disabled={run.stop_requested || stopMutation.isPending}
                                onClick={() => stopMutation.mutate(run.id)}
                              >
                                {run.stop_requested ? (
                                  <>
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                    Stopping
                                  </>
                                ) : (
                                  <><Square className="mr-1 h-3 w-3" />Stop</>
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={() => setExpandedRun(
                                expandedRun === run.id ? null : run.id,
                              )}
                            >
                              {isExpanded ? (
                                <><ChevronUp className="mr-1 h-3 w-3" />Hide</>
                              ) : (
                                <><ChevronDown className="mr-1 h-3 w-3" />Details</>
                              )}
                            </Button>
                            <Button
                              size="sm" variant="ghost" className="h-7 px-2 text-xs"
                              onClick={async () => {
                                try {
                                  const text = await schedulerApi.downloadRunLog(run.id)
                                  downloadText(text, `run_${run.id}_${run.pipeline}.txt`)
                                } catch (e) {
                                  setError(getApiErrorMessage(e))
                                }
                              }}
                            >
                              <Download className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="border-b bg-muted/20">
                          <td colSpan={7} className="p-0">
                            <RunDetailPanel
                              run={run}
                              detail={runDetail?.run.id === run.id ? runDetail : null}
                            />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

        </CardContent>
      </Card>
    </div>
  )
}
